import { useMatomo } from "@jonkoops/matomo-tracker-react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextareaAutosize,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import LoadPatientRecordsFromCSV from "../CSVParser";

const LoadDosingInfoDialog = ({
  loadMeasurementsDialog,
  handleLoadMeasurementsDialogClose,
  fileLoadErrors,
  setFileLoadErrors,
  handleImportMeasurements,
}) => {
  const theme = useTheme();
  const { trackEvent } = useMatomo();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [textAreavalue, setTextAreavalue] = useState("");
  const [dataSource, setDataSource] = useState("");
  const [fileData, setFileData] = useState([]);

  const handleMeasurementsData = (e) => {
    setDataSource("text");
    setTextAreavalue(e.target.value);
  };

  useEffect(() => {
    if (fileData.length > 0) {
      setDataSource("file");
    } else {
      setDataSource("");
    }
  }, [fileData]);

  useEffect(() => {
    if (textAreavalue !== "") {
      setDataSource("text");
    } else {
      setDataSource("");
    }
  }, [textAreavalue]);

  const formatDateTime = (datePart, time) => {
    const year = datePart.getFullYear();
    const month =
      datePart.getMonth() + 1 < 10
        ? "0" + (datePart.getMonth() + 1)
        : datePart.getMonth() + 1;
    const date =
      datePart.getDate() < 10 ? "0" + datePart.getDate() : datePart.getDate();

    //format time - prepend '0'
    const formattedTime = ("0" + time).slice(-5);

    return year + "-" + month + "-" + date + "T" + formattedTime;
  };

  const handleParseData = () => {
    if (dataSource === "text") {
      let measData = [];
      let measTimeInHours = true;
      try {
        const rows = textAreavalue.split("\n");
        const re = /[ ,\t]/gm;
        rows.forEach((row) => {
          if (row.trim()) {
            let rowData = row.split(re);
            rowData = rowData.map((rd) => rd.trim()).filter((rd) => rd);

            let parsedRow = {};
            if (rowData.length === 4) {
              measTimeInHours = false;
              const datePart = new Date(rowData[0]);
              const timePart = rowData[1];
              const formattedDateTime = formatDateTime(datePart, timePart);
              parsedRow = {
                time: formattedDateTime,
                conc: rowData[2],
                creat: rowData[3],
              };
            } else {
              measTimeInHours = true;
              if (rowData[0].includes("/")) {
                throw new Error(
                  "Date and Time is required when entering date and time.",
                );
              }
              parsedRow = {
                time: rowData[0],
                conc: rowData[1],
                creat: rowData[2],
              };
            }
            measData.push(parsedRow);
          }
        });
      } catch (error) {
        console.error("Errror parsing the measurements data", error);
      }
      const defaultCreatUnitInmml = measData.every((ob) => ob["creat"] >= 10);
      handleImportMeasurements(
        measData,
        measTimeInHours,
        defaultCreatUnitInmml,
      );
    } else if (dataSource === "file") {
      let measData = [];
      let measTimeInHours = true;
      try {
        for (let i = 1; i < fileData.length; i++) {
          if (fileData[i].length > 0) {
            if (fileData[i][0].includes("-") || fileData[i][0].includes("/")) {
              measTimeInHours = false;
            }
            let parsedRow = {};
            if (measTimeInHours) {
              parsedRow = {
                time: fileData[i][0],
                conc: fileData[i][1],
                creat: fileData[i][2],
              };
            } else {
              const parsedDateTime = new Date(fileData[i][0]);
              parsedRow = {
                time: formatDateTime(
                  parsedDateTime,
                  parsedDateTime.getHours() + ":" + parsedDateTime.getMinutes(),
                ),
                conc: fileData[i][1],
                creat: fileData[i][2],
              };
            }
            measData.push(parsedRow);
          }
        }
      } catch (error) {
        console.error("Errror parsing the measurements data", error);
      }
      const defaultCreatUnitInmml = measData.every((ob) => ob["creat"] >= 10);
      handleImportMeasurements(
        measData,
        measTimeInHours,
        defaultCreatUnitInmml,
      );
    }
  };

  return (
    <Dialog
      open={loadMeasurementsDialog}
      onClose={handleLoadMeasurementsDialogClose}
      aria-labelledby="Load Patient Data"
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: "95vw", sm: 700 },
        },
      }}
    >
      <DialogTitle id="load-patient-data-dialog-title">
        Import Measurements
      </DialogTitle>
      <DialogContent>
        <Container>
          <Typography variant="subtitle2">
            Enter or paste the measurements
          </Typography>
          <Typography variant="caption">
            Accepted date and time formats: <b>MM/DD/YYYY HH:MM</b> or{" "}
            <b>MM/DD/YYYY,HH:MM</b>
          </Typography>
          <Typography variant="caption" display="block">
            E.g., <br />
            03/10/2022 13:24 95 0.52 (for measurements with date and time),
            <br />
            24 0.95 0.32 (for measurements with hour)
          </Typography>
        </Container>
        <List>
          <ListItem>
            <TextareaAutosize
              value={textAreavalue}
              onChange={handleMeasurementsData}
              aria-label="text area"
              minRows={10}
              placeholder="paste measurements here"
              style={{
                width: "100%",
                minWidth: 300,
                maxWidth: 700,
                fontSize: 16,
                padding: 8,
                borderRadius: 4,
                border: "1px solid #ccc",
                resize: "vertical",
              }}
            />
          </ListItem>
        </List>

        <List
          subheader={
            <ListSubheader component="div" id="load-patient-data">
              Import from file (.csv)
            </ListSubheader>
          }
        >
          <ListItem>
            <ListItemIcon>
              <AttachFileIcon />
            </ListItemIcon>
            <ListItemText>
              <LoadPatientRecordsFromCSV
                infoText="Select File"
                setFileData={setFileData}
                setFileLoadErrors={setFileLoadErrors}
                setDataSource={setDataSource}
              />
            </ListItemText>
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 3,
          pb: 2,
        }}
      >
        <Button
          color="primary"
          onClick={() => {
            trackEvent({
              category: "Data Entry",
              action: "Parse Measurements",
              name: "Parse Measurements",
            });
            handleParseData();
          }}
          variant="contained"
          disabled={dataSource === ""}
        >
          Upload{" "}
          {dataSource === "text" ? "text" : dataSource === "file" ? "file" : ""}
        </Button>
        <Button color="primary" onClick={handleLoadMeasurementsDialogClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoadDosingInfoDialog;
